/**
 * Centralized Error Logging System
 * Tracks errors, warnings, and debug info across frontend and backend
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'fatal';
export type LogContext = 'frontend' | 'backend' | 'shared';

export interface LogEntry {
  timestamp: number;
  level: LogLevel;
  context: LogContext;
  message: string;
  data?: any;
  stack?: string;
  userAgent?: string;
}

export class Logger {
  private logs: LogEntry[] = [];
  private maxLogs = 1000; // Keep last 1000 logs
  private context: LogContext;
  private enableConsole: boolean;
  private logLevelPriority: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
    fatal: 4
  };
  private minLevel: LogLevel;

  constructor(context: LogContext, options: { enableConsole?: boolean; minLevel?: LogLevel } = {}) {
    this.context = context;
    this.enableConsole = options.enableConsole ?? true;
    this.minLevel = options.minLevel ?? 'debug';
  }

  private shouldLog(level: LogLevel): boolean {
    return this.logLevelPriority[level] >= this.logLevelPriority[this.minLevel];
  }

  private log(level: LogLevel, message: string, data?: any, error?: Error) {
    if (!this.shouldLog(level)) return;

    const entry: LogEntry = {
      timestamp: Date.now(),
      level,
      context: this.context,
      message,
      data,
      stack: error?.stack,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined
    };

    // Add to internal log buffer
    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Console output with color coding
    if (this.enableConsole) {
      const emoji = this.getEmoji(level);
      const timestamp = new Date(entry.timestamp).toISOString();
      const prefix = `${emoji} [${timestamp}] [${this.context.toUpperCase()}]`;

      switch (level) {
        case 'debug':
          console.debug(prefix, message, data || '');
          break;
        case 'info':
          console.info(prefix, message, data || '');
          break;
        case 'warn':
          console.warn(prefix, message, data || '');
          break;
        case 'error':
        case 'fatal':
          console.error(prefix, message, data || '');
          if (error?.stack) {
            console.error('Stack trace:', error.stack);
          }
          break;
      }
    }
  }

  private getEmoji(level: LogLevel): string {
    switch (level) {
      case 'debug': return '🔍';
      case 'info': return 'ℹ️';
      case 'warn': return '⚠️';
      case 'error': return '❌';
      case 'fatal': return '💥';
    }
  }

  debug(message: string, data?: any) {
    this.log('debug', message, data);
  }

  info(message: string, data?: any) {
    this.log('info', message, data);
  }

  warn(message: string, data?: any) {
    this.log('warn', message, data);
  }

  error(message: string, error?: Error | any, data?: any) {
    this.log('error', message, data, error instanceof Error ? error : undefined);
  }

  fatal(message: string, error?: Error | any, data?: any) {
    this.log('fatal', message, data, error instanceof Error ? error : undefined);
  }

  // Get logs filtered by level
  getLogs(level?: LogLevel): LogEntry[] {
    if (!level) return [...this.logs];
    return this.logs.filter(log => log.level === level);
  }

  // Get logs since timestamp
  getLogsSince(timestamp: number): LogEntry[] {
    return this.logs.filter(log => log.timestamp >= timestamp);
  }

  // Get recent errors
  getRecentErrors(count = 10): LogEntry[] {
    return this.logs
      .filter(log => log.level === 'error' || log.level === 'fatal')
      .slice(-count);
  }

  // Export logs as JSON
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  // Clear logs
  clear() {
    this.logs = [];
  }

  // Get log summary
  getSummary(): { total: number; byLevel: Record<LogLevel, number> } {
    const summary = {
      total: this.logs.length,
      byLevel: {
        debug: 0,
        info: 0,
        warn: 0,
        error: 0,
        fatal: 0
      } as Record<LogLevel, number>
    };

    this.logs.forEach(log => {
      summary.byLevel[log.level]++;
    });

    return summary;
  }
}

// Global logger instances
export const frontendLogger = new Logger('frontend', { minLevel: 'debug' });
export const backendLogger = new Logger('backend', { minLevel: 'info' });

// Utility to log unhandled errors
export function setupGlobalErrorHandling(logger: Logger) {
  if (typeof window !== 'undefined') {
    // Frontend error handling
    window.addEventListener('error', (event) => {
      logger.fatal('Unhandled error', event.error, {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      });
    });

    window.addEventListener('unhandledrejection', (event) => {
      logger.fatal('Unhandled promise rejection', event.reason, {
        promise: event.promise
      });
    });
  }
}
